import Dates from '@/constants/Dates';
import {
  InfoCircleFilled,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Radio,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { StyledPaymentForm, CustomCard } from './PaymentForm.styled';
import PaymentMethods from './PaymentMethods/PaymentMethods';
import usePaymentForm from './usePaymentForm.hook';
import PromotionCode from './PromotionCode/PromotionCode';
import { capitalize } from '@/modules/stringUtils';
import defaultCreateSubscriptionPayment from '@/api/subscriptions/createSubscriptionPayment.endpoint';
import { PaymentFormContextProvider } from './PaymentFormContext';

const { Text } = Typography;

export default function PaymentForm({
  title = 'Choose how long you want to subscribe',
  subscriptionType,
  existingSubscription,
  bordered,
  onPaymentCompletedUrl,
  onPaymentCompleted = () => {},
  createSubscriptionPayment = defaultCreateSubscriptionPayment,
}) {
  const { state, actions } = usePaymentForm({
    subscriptionType,
    existingSubscription,
  });

  return (
    <PaymentFormContextProvider
      value={{
        state,
        actions: {
          onPaymentCompletedUrl,
          onPaymentCompleted,
          createSubscriptionPayment,
        },
      }}
    >
      <StyledPaymentForm>
        <CustomCard className={bordered ? 'bordered' : ''}>
          <div className="left-panel">
            <h3>{title}</h3>
            <Radio.Group
              name="radiogroup"
              defaultValue="year"
              value={state.quantityUnit}
            >
              <div className="quantity-unit-options">
                <PriceRadio
                  time="year"
                  unit={state.quantityUnit}
                  value={state.quantityYears}
                  price={subscriptionType.yearPrice}
                  onChange={(event) =>
                    actions.setQuantityUnit(event.target.value)
                  }
                  onTimeSelect={(quantity) =>
                    actions.setQuantity(quantity, 'Years')
                  }
                />
                <PriceRadio
                  time="month"
                  unit={state.quantityUnit}
                  value={state.quantityMonths}
                  price={subscriptionType.monthPrice}
                  onChange={(event) =>
                    actions.setQuantityUnit(event.target.value)
                  }
                  onTimeSelect={(quantity) =>
                    actions.setQuantity(quantity, 'Months')
                  }
                />
              </div>
            </Radio.Group>
            <div className="text-left mt">
              <Checkbox
                disabled={!state.quantityUnit}
                checked={state.autoRenew}
                onChange={(event) => actions.setAutoRenew(event.target.checked)}
              >
                <Text>
                  I want to auto renew my subscription each {state.quantityUnit}{' '}
                </Text>
                <Tooltip
                  title={`Your subscription will automatically renew each ${state.quantityUnit}. You can cancel auto renew any time`}
                >
                  <InfoCircleFilled className="info" />
                </Tooltip>
                <br />
                {state.quantityUnit ? (
                  <Text type="secondary">
                    {state.autoRenew ? 'Auto renew' : 'Expire'} on:{' '}
                    {dayjs(state.expirationDate).format(Dates.defaultFormat)}
                  </Text>
                ) : null}
              </Checkbox>
            </div>
            <Divider />
            {state.quantityUnit ? (
              <PromotionCode
                promotionCode={state.promotionCode}
                setPromotionCode={actions.setPromotionCode}
                totalWithoutDiscount={(
                  state.totalWithoutDiscount -
                  state.existingSubscriptionDiscount
                ).toFixed(2)}
                total={state.total}
              />
            ) : null}
          </div>
          <div className="right-panel">
            {state.quantityUnit ? (
              <>
                <div className="total-container">
                  {state.quantityUnit &&
                  state.existingSubscriptionDiscount > 0 ? (
                    <Alert
                      className="discount-alert"
                      description={
                        <Space direction="vertical">
                          <div>
                            Subscription price:{' '}
                            <strong>${state.totalWithoutDiscount}</strong>
                          </div>

                          <div>
                            Discount for your existing subscription: -
                            <strong>
                              ${state.existingSubscriptionDiscount}
                            </strong>
                          </div>
                        </Space>
                      }
                    ></Alert>
                  ) : null}
                  <h3>
                    <Text className="total-label">Total to pay now: </Text>
                    <Text
                      strong
                      className={`total ${existingSubscription ? '' : 'big'}`}
                    >
                      ${state.total}
                    </Text>
                  </h3>
                </div>
                <CustomCard className="bordered">
                  <PaymentMethods
                    subscriptionTypeId={subscriptionType?.id}
                    amount={state.total}
                    subscriptionParams={{
                      quantity: state.quantity,
                      quantityUnit: state.quantityUnit,
                      autoRenew: state.autoRenew,
                    }}
                    promotionCode={state.promotionCode}
                  />
                </CustomCard>
              </>
            ) : null}
          </div>
        </CustomCard>
      </StyledPaymentForm>
    </PaymentFormContextProvider>
  );
}

function PriceRadio({
  time = 'month',
  unit,
  value,
  price,
  onChange = () => {},
  onTimeSelect = () => {},
}) {
  const active = unit == time;
  return (
    <div
      className={`quantity-unit-option ${active ? 'active' : ''} `}
      onClick={() => onChange({ target: { value: time } })}
    >
      <Radio value={time} onChange={onChange}>
        <span className="quantity-unit-text">
          <span className="price">${price}</span> / {`${time} `}
        </span>
      </Radio>
      <div className="quantity-unit-buttons">
        <Button
          disabled={!active || value == 1}
          type="primary"
          icon={<MinusOutlined />}
          onClick={() => onTimeSelect(value - 1)}
        />
        <span className="quantity-unit-value">
          {value} {value == 1 ? capitalize(time) : capitalize(`${time}s`)}
        </span>
        <Button
          disabled={unit != time}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => onTimeSelect(value + 1)}
        />
      </div>
    </div>
  );
}
